import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './shared/contexts/AuthContext';
import Auth from './shared/components/Auth';
import AuthCallback from './shared/components/auth/AuthCallback';
import Layout from './shared/components/layout/Layout';
import ProtectedRoute from './features/ProtectedRoute';
import Home from './features/home/Home';
import Chat from './features/chat/components/Chat';
import Account from './features/account/components/Account';
import BiblePage from './routes/BiblePage';
import Bookmarks from './features/bookmarks/components/Bookmarks';
import Discover from './features/discover/components/Discover';
import Support from './features/support/components/Support';
import Terms from './features/marketing/components/Terms';
import Privacy from './features/marketing/components/Privacy';
import { Toaster } from 'react-hot-toast';
import { GlobalErrorBoundary } from './shared/components/error/GlobalErrorBoundary';
import { AuthErrorBoundary } from './shared/components/auth/AuthErrorBoundary';
import { SubscriptionGuard } from './shared/components/guards/SubscriptionGuard';
import PricingPlans from './shared/components/paywall/PricingPlans';
import Subscribe from './shared/components/paywall/Subscribe';
import { OnboardingContainer } from './features/onboarding/components';
import PublicChat from './features/chat/components/PublicChat';

function AppRoutes() {
  const { user, isInitialized } = useAuth();
  const location = useLocation();

  // Don't render any routes until auth is initialized
  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/support" element={<Support />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route 
        path="/login" 
        element={
          user ? <Navigate to={location.state?.from?.pathname || '/'} replace /> : <Auth />
        } 
      />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/shared/:publicId" element={<PublicChat />} />
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingContainer /></ProtectedRoute>} />
      <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        {/* Protected by subscription */}
        <Route path="/" element={
          <SubscriptionGuard>
            <Home />
          </SubscriptionGuard>
        } />
        <Route path="/chat/:id" element={
          <SubscriptionGuard>
            <Chat />
          </SubscriptionGuard>
        } />
        {/* Not protected by subscription */}
        <Route path="/account" element={<Account />} />
        <Route path="/pricing" element={<PricingPlans />} />
        <Route path="/subscribe" element={<Subscribe />} />
        {/* Protected by subscription */}
        <Route path="/bible" element={
          <SubscriptionGuard>
            <Navigate to="/bible/genesis/1" replace />
          </SubscriptionGuard>
        } />
        <Route path="/bible/:book/:chapter" element={
          <SubscriptionGuard>
            <BiblePage />
          </SubscriptionGuard>
        } />
        <Route path="/bookmarks" element={
          <SubscriptionGuard>
            <Bookmarks />
          </SubscriptionGuard>
        } />
        <Route path="/explore" element={
          <SubscriptionGuard>
            <Discover />
          </SubscriptionGuard>
        } />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <GlobalErrorBoundary>
      <HelmetProvider>
        <AuthProvider>
          <Router>
            <AuthErrorBoundary>
              <AppRoutes />
              <Toaster />
            </AuthErrorBoundary>
          </Router>
        </AuthProvider>
      </HelmetProvider>
    </GlobalErrorBoundary>
  );
}